import { viewFetch as fetch } from '../view-as.js';
import type { AddedWork, AddedWorkInput, MomentumFilters, MomentumPeople, MomentumSource, MomentumView as ApiMomentumView, QuietDeal } from '../../../api/src/momentum/types.js';
import type { PulseSelection, PulseView } from '../../../api/src/momentum/pulse-types.js';
export type { PulseBin, PulseChannel, PulseCounts, PulseData, PulseEvent, PulsePerson, PulseSelection, PulseView } from '../../../api/src/momentum/pulse-types.js';
export type { AddedWork, DealLine, IntelligenceLine, MomentumEvent, MomentumFilters, MomentumKind, MomentumPeople, MomentumPerson, QuietDeal } from '../../../api/src/momentum/types.js';

export interface WaitingDeal extends Omit<QuietDeal, 'lastAt'> {
  since: string;
  reasons: Array<{ kind: 'unanswered' | 'decision' | 'silent'; since: string; days: number; decision?: string }>;
}
/** Absent until the API that reads it is live: the page draws no Waiting on us until then (REQ-2026-10-12-31). */
export interface MomentumView extends Omit<ApiMomentumView, 'waiting'> {
  waiting?: WaitingDeal[];
  feedSource?: 'stored' | 'live';
  feedComputedAt?: string;
}

export class MomentumReadError extends Error { constructor(message:string,readonly status:number){super(message);} }

async function request<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const r = await fetch(path, { credentials: 'same-origin', signal: signal ?? null, ...(body === undefined ? {} : {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  }) });
  if (!r.ok) {
    const error = await r.json().catch(() => ({})) as { error?: string };
    throw new MomentumReadError(error.error ?? 'Cobrain could not be reached.',r.status);
  }
  return r.json() as Promise<T>;
}
const storeQuery = (store: string) => `store=${encodeURIComponent(store)}`;
export const momentumApi = {
  pulseSource: (store:string,id:string,signal?:AbortSignal)=>request<MomentumSource>(`/api/momentum/pulse/source/${encodeURIComponent(id)}?${storeQuery(store)}`,undefined,signal),
  pulse: (store: string, selection: PulseSelection, signal?: AbortSignal) => {
    const q = new URLSearchParams({store,days:String(selection.days)});
    for (const key of ['person','channel','from'] as const) if(selection[key]) q.set(key,selection[key]);
    return request<PulseView>(`/api/momentum/pulse?${q}`,undefined,signal);
  },
  feed: (store: string, before?: string, signal?: AbortSignal, exact?: boolean) => request<MomentumView>(`/api/momentum?${storeQuery(store)}${before ? `&before=${encodeURIComponent(before)}` : ''}${exact ? '&exact=1' : ''}`, undefined, signal),
  filters: (store: string, filters: MomentumFilters) => request('/api/momentum/filters', { store, filters }),
  people: (store: string, days: number) => request<MomentumPeople>(`/api/momentum/people?${storeQuery(store)}&days=${days}`),
  work: (store: string, person?: string) => request<{ work: AddedWork[] }>(`/api/momentum/work?${storeQuery(store)}${person ? `&person=${encodeURIComponent(person)}` : ''}`),
  addWork: (store: string, input: AddedWorkInput) => request<{ id: string }>('/api/momentum/work', { store, ...input }),
  undoWork: (store: string, id: string) => request(`/api/momentum/work/${encodeURIComponent(id)}/undo`, { store }),
};
